/* eslint-disable max-lines-per-function, max-lines */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { ArrowDown } from 'lucide-react';

import type { ChatSidebarMode } from '@/contexts/ChatSidebarContextDef';
import type { ChatMessage } from '@/hooks/useChat';
import { useTrackScrollToBottom } from '@/hooks/useTrackScrollToBottom';
import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

import { MarkdownRenderer } from '@/components/MarkdownRenderer';

import { MessageFileCard } from './MessageFileCard';
import { ThinkingIndicator } from './ThinkingIndicator';

const POST_PIN_USER_SCROLL_GRACE_MS = 900;

interface MessageListProps {
  messages: ChatMessage[];
  isResponding?: boolean;
  conversationId?: string | null;
  mode?: ChatSidebarMode;
}

const ERROR_MESSAGES: Record<string, string> = {
  rate_limit:
    'Rate limit reached - please wait a moment before sending more messages',
  connection: 'Connection issue - check your network and try again',
  unknown: "Didn't get a reply - please try again",
  default: "Didn't get a reply - please try again",
};

const getErrorMessage = (errorType?: string) =>
  ERROR_MESSAGES[errorType ?? 'default'] ?? ERROR_MESSAGES.default;

interface FileAttachmentMeta {
  filename: string;
  sizeBytes?: number;
  fileSize?: number;
}

const getFileAttachments = (
  message: ChatMessage
): FileAttachmentMeta[] | null => {
  const attachments = message.metadata?.fileAttachments;
  if (!Array.isArray(attachments) || attachments.length === 0) {
    return null;
  }
  return attachments as FileAttachmentMeta[];
};

export const MessageList = ({
  messages,
  isResponding,
  conversationId,
  mode,
}: MessageListProps) => {
  const {
    containerRef: messagesContainerRef,
    isAtBottom,
    isAtBottomRef,
    scrollToBottom: scrollContainerToBottom,
    scrollToElement,
  } = useTrackScrollToBottom();
  const previousMessageCountRef = useRef(messages.length);
  const previousConversationIdRef = useRef<string | null | undefined>(
    undefined
  );
  const scrolledToUserMessageRef = useRef(false);
  const scrollTargetRef = useRef<HTMLDivElement | null>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  const messageRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  const spacerUpdateRafRef = useRef<number | null>(null);
  const [isUserMessagePinned, setIsUserMessagePinned] = useState(false);
  const pinnedSinceRef = useRef(-Infinity);

  const setMessageRef = useCallback(
    (id: string, element: HTMLDivElement | null) => {
      if (element) {
        messageRefsMap.current.set(id, element);
      } else {
        messageRefsMap.current.delete(id);
      }
    },
    []
  );

  const updateSpacerHeight = useCallback(() => {
    const container = messagesContainerRef.current;
    const spacer = spacerRef.current;
    const target = scrollTargetRef.current;
    if (!container || !spacer || !target) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const spacerRect = spacer.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const paddingTop = parseFloat(getComputedStyle(container).paddingTop) || 0;
    const marginTop = parseFloat(getComputedStyle(target).marginTop) || 0;

    const scrollTarget =
      container.scrollTop +
      (targetRect.top - containerRect.top) -
      paddingTop -
      marginTop;
    const contentEndInScroll =
      container.scrollTop + (spacerRect.top - containerRect.top);

    const needed = Math.max(
      0,
      scrollTarget + container.clientHeight - contentEndInScroll
    );
    spacer.style.height = `${needed}px`;

    if (needed === 0) {
      scrolledToUserMessageRef.current = false;
      scrollTargetRef.current = null;
      setIsUserMessagePinned(false);
    }
  }, [messagesContainerRef]);

  const scheduleSpacerUpdate = useCallback(() => {
    if (spacerUpdateRafRef.current !== null) {
      return;
    }
    spacerUpdateRafRef.current = requestAnimationFrame(() => {
      spacerUpdateRafRef.current = null;
      updateSpacerHeight();
    });
  }, [updateSpacerHeight]);

  const clearSpacer = useCallback(() => {
    scrolledToUserMessageRef.current = false;
    scrollTargetRef.current = null;
    setIsUserMessagePinned(false);
    if (spacerUpdateRafRef.current !== null) {
      cancelAnimationFrame(spacerUpdateRafRef.current);
      spacerUpdateRafRef.current = null;
    }
    if (spacerRef.current) {
      spacerRef.current.style.height = '0px';
    }
  }, []);

  useEffect(() => {
    return () => {
      if (spacerUpdateRafRef.current !== null) {
        cancelAnimationFrame(spacerUpdateRafRef.current);
        spacerUpdateRafRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }
    const handleScroll = () => {
      const sincePinned = performance.now() - pinnedSinceRef.current;
      if (sincePinned < POST_PIN_USER_SCROLL_GRACE_MS) {
        return;
      }
      setIsUserMessagePinned(false);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [messagesContainerRef]);

  const handleJumpToBottom = useCallback(() => {
    scrolledToUserMessageRef.current = false;
    scrollTargetRef.current = null;
    setIsUserMessagePinned(false);
    scrollContainerToBottom();
  }, [scrollContainerToBottom]);

  useLayoutEffect(() => {
    if (conversationId !== previousConversationIdRef.current) {
      previousConversationIdRef.current = conversationId;
      previousMessageCountRef.current = messages.length;
      clearSpacer();
      scrollContainerToBottom();
      return;
    }

    if (scrolledToUserMessageRef.current) {
      scheduleSpacerUpdate();
    }

    const previousCount = previousMessageCountRef.current;
    previousMessageCountRef.current = messages.length;

    if (messages.length <= previousCount) {
      return;
    }

    const newMessages = messages.slice(previousCount);
    const lastNewUserMessage = newMessages.findLast(
      (message) => message.role === 'user'
    );

    if (lastNewUserMessage) {
      const element = messageRefsMap.current.get(lastNewUserMessage.id);
      if (element) {
        scrollTargetRef.current = element;
        scrolledToUserMessageRef.current = true;
        setIsUserMessagePinned(true);
        pinnedSinceRef.current = performance.now();
        updateSpacerHeight();
        scrollToElement(element);
        return;
      }
    }

    if (scrolledToUserMessageRef.current) {
      return;
    }

    if (isAtBottomRef.current) {
      scrollContainerToBottom();
    }
  }, [
    messages,
    conversationId,
    scrollContainerToBottom,
    scrollToElement,
    updateSpacerHeight,
    scheduleSpacerUpdate,
    clearSpacer,
    isAtBottomRef,
  ]);

  const isJumpToBottomHidden = isAtBottom || isUserMessagePinned;

  return (
    <div className="relative flex-1 min-h-0 flex flex-col">
      <div
        ref={messagesContainerRef}
        className={cn(
          'py-6 px-4 space-y-6 flex-1 overflow-auto',
          mode === 'sidebar' && 'px-6'
        )}
      >
        {messages.map((message) => {
          const isUser = message.role === 'user';
          return (
            <div
              key={message.id}
              ref={(node) => setMessageRef(message.id, node)}
              className={cn(
                'flex flex-col max-w-[calc(theme(maxWidth.3xl)-3rem)] mx-auto',
                isUser ? 'items-end' : 'items-start pb-8'
              )}
            >
              <Card
                className={cn(
                  isUser
                    ? 'bg-primary/10 dark:bg-primary/20 text-foreground max-w-[80%]'
                    : 'border-none shadow-none bg-transparent sm:max-w-[80%]'
                )}
              >
                <div className={cn(isUser && 'p-4')}>
                  {isUser ? (
                    <div className="whitespace-pre-wrap break-words">
                      {message.content}
                      {message.status === 'failed' && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          {getErrorMessage(message.errorType)}
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      {message.content && (
                        <MarkdownRenderer content={message.content} />
                      )}
                      {!message.content &&
                        isResponding &&
                        message.status === 'pending' && <ThinkingIndicator />}
                      {message.status === 'interrupted' && (
                        <p className="mt-2 text-xs text-muted-foreground italic">
                          You stopped this response
                        </p>
                      )}
                    </>
                  )}
                </div>
              </Card>
              {isUser &&
                (() => {
                  const fileAttachments = getFileAttachments(message);
                  if (!fileAttachments) {
                    return null;
                  }
                  return (
                    <div className="mt-2 flex max-w-[80%] flex-wrap justify-end gap-1.5">
                      {fileAttachments.map((file, index) => (
                        <MessageFileCard
                          key={`${message.id}-${index}-${file.filename}`}
                          filename={file.filename}
                          sizeBytes={file.sizeBytes ?? file.fileSize ?? 0}
                        />
                      ))}
                    </div>
                  );
                })()}
            </div>
          );
        })}

        <div ref={spacerRef} />
      </div>

      <Button
        onClick={handleJumpToBottom}
        variant="outline"
        size="icon"
        tabIndex={isJumpToBottomHidden ? -1 : 0}
        aria-hidden={isJumpToBottomHidden}
        className={cn(
          'absolute bottom-3 right-3 h-8 w-8 rounded-full shadow-md bg-background/90 backdrop-blur-sm transition-[opacity,transform] duration-300 ease-out',
          isJumpToBottomHidden
            ? 'opacity-0 scale-75 pointer-events-none'
            : 'opacity-100 scale-100'
        )}
        aria-label="Scroll to bottom"
      >
        <ArrowDown className="h-4 w-4" />
      </Button>
    </div>
  );
};
